import {getUser} from '../users/selectors'
import {getMerchant} from '../merchants/selectors'
import {getSite} from '../sites/selectors'
import {getStartDate, getEndDate, getDynamicDateType} from './utils'
import moment from 'moment'

export function getCurrentUser(state) {
  return getUser(state, state.currentUser.userId)
}

export function getAccessToken(state) {
  const currentUser = getCurrentUser(state)
  if (!currentUser) {
    return null
  }

  return state.currentUser.token
}

export function getCurrentUserName(state) {
  const currentUser = getCurrentUser(state)
  if (!currentUser) {
    return null
  }

  return currentUser.name || null
}

export function getCurrentSite(state) {
  if (!state.currentUser.siteId) {
    return null
  }

  return getSite(state, state.currentUser.siteId)
}

export function getAggregatedSites(state, crossSiteMode) {
  const merchantSite = getCurrentSite(state)
  const merchant = getCurrentMerchant(state)

  if (!merchant || !merchantSite) return []

  const merchantId = merchant.id
  const {
    currentUser: {aggregatedSites},
    sites: {list: siteList}
  } = state

  if (crossSiteMode) {
    if (siteList < 3) return Object.keys(siteList)

    const sites = aggregatedSites ? aggregatedSites[merchantId] : []
    if (!sites || !sites.length) return Object.keys(siteList)

    return sites
  } else {
    return merchantSite.id
  }
}

export function getCurrentMerchant(state) {
  if (!state.currentUser.merchantId) return null

  return getMerchant(state, state.currentUser.merchantId)
}

export function getCurrentTimePeriod(state) {
  const merchant = getCurrentMerchant(state)
  if (!merchant) return 'range'
  const {timePeriod} = merchant
  if (!timePeriod) return 'range'
  const {selectedTimePeriod} = state.currentUser
  return selectedTimePeriod || timePeriod
}

export function getEndTotalDate(range, originalEndDate, state) {
  const today = state.today
  const timePeriod = getCurrentTimePeriod(state)
  const todayWithinRange = moment(today).isBetween(
    range.start,
    range.end,
    null,
    '[]'
  )

  if (!todayWithinRange) return originalEndDate

  const diffInYears =
    moment(today).clone().get('year') -
    moment(originalEndDate).clone().get('year')
  const diffInMonth =
    moment(today).clone().get('month') -
    moment(originalEndDate).clone().get('month')
  const diffInWeeks =
    moment(originalEndDate).isoWeek() - moment(today).isoWeek()

  switch (timePeriod) {
    case 'year':
      return moment(today).clone().subtract(diffInYears, 'years').endOf('day')
    case 'month':
      return moment(today).clone().subtract(diffInMonth, 'months').endOf('day')
    case 'week':
      return moment(today).clone().subtract(diffInWeeks, 'weeks').endOf('day')
  }
}

export function getSelectedDateRange(state) {
  const startDate = getStartDate(state)
  const endDate = getEndDate(state, startDate)
  const dynamicDateType = getDynamicDateType(state)

  return {
    startDate,
    endDate,
    dynamicDateType
  }
}

export function getTimePeriodRanges(state) {
  const {
    today,
    currentUser: {
      selectedTimePeriodStartRange: startRange,
      selectedTimePeriodEndRange: endRange
    }
  } = state
  const timePeriod = getCurrentTimePeriod(state)
  if (!timePeriod) return null

  if (startRange && endRange) {
    return {
      start: startRange,
      end: endRange
    }
  }

  switch (timePeriod) {
    case 'year':
      const pastYear = moment(today).clone().subtract(1, 'years')
      return {
        start: {
          start: moment(today).clone().startOf('year').toDate(),
          end: moment(today).clone().endOf('year').toDate()
        },
        end: {
          start: moment(pastYear).clone().startOf('year').toDate(),
          end: moment(pastYear).clone().endOf('year').toDate()
        }
      }

    case 'month':
      const pastMonth = moment(today).clone().subtract(1, 'months')
      return {
        start: {
          start: moment(today).clone().startOf('month').toDate(),
          end: moment(today).clone().endOf('month').toDate()
        },
        end: {
          start: moment(pastMonth).clone().startOf('month').toDate(),
          end: moment(pastMonth).clone().endOf('month').toDate()
        }
      }

    case 'week':
      const pastWeek = moment(today).clone().subtract(1, 'weeks')
      return {
        start: {
          start: moment(today).clone().startOf('isoWeek').toDate(),
          end: moment(today).clone().endOf('isoWeek').toDate()
        },
        end: {
          start: moment(pastWeek).clone().startOf('isoWeek').toDate(),
          end: moment(pastWeek).clone().endOf('isoWeek').toDate()
        }
      }
    case 'range':
      if (__DEFAULT_START_DATE__ && __DEFAULT_END_DATE__) {
        let newStartDate = moment(__DEFAULT_START_DATE__).toDate()
        let newEndDate = moment(__DEFAULT_END_DATE__).toDate()
        const typeRange = 'days'
        const dayDiff = moment(moment(newEndDate).format()).diff(
          moment(newStartDate).format(),
          typeRange
        )
        const startRange = {
          start: newStartDate,
          end: newEndDate
        }
        const endDayStart = moment(newStartDate)
          .subtract(1 + dayDiff, typeRange)
          .toDate()
        const endDayEnd = moment(newStartDate)
          .endOf('day')
          .subtract(1, typeRange)
          .endOf(typeRange)
          .toDate()
        const endRange = {
          start: endDayStart, end: endDayEnd
        }
        return {
          start: startRange,
          end: endRange
        }
      }
      const pastDays = moment(today).clone().subtract(1, 'days')
      return {
        start: {
          start: moment(pastDays).clone().startOf('days').toDate(),
          end: moment(pastDays).clone().endOf('days').toDate()
        },
        end: {
          start: moment(pastDays)
            .clone()
            .subtract(1, 'days')
            .startOf('days')
            .toDate(),
          end: moment(pastDays)
            .clone()
            .subtract(1, 'days')
            .endOf('days')
            .toDate()
        }
      }
  }
}

export function getConverionType(state) {
  const {
    currentUser: {conversionType}
  } = state

  return conversionType
}

export function getOrderedTimePeriodRanges(state) {
  const {start: startRange, end: endRange} = getTimePeriodRanges(state)

  if (moment(startRange.start).isBefore(moment(endRange.start))) {
    return {
      start: endRange,
      end: startRange,
      switched: true
    }
  } else {
    return {
      start: startRange,
      end: endRange
    }
  }
}

export function isAdminUser(state) {
  const merchant = getCurrentMerchant(state)
  if (!merchant) return false

  const currentUser = getCurrentUser(state)

  return Boolean(
    currentUser &&
      currentUser.roles &&
      currentUser.roles.find(
        ({role, merchantId}) =>
          role === 'admin' && (merchantId === '*' || merchantId === merchant.id)
      )
  )
}

export function isDemoUser(state) {
  const merchant = getCurrentMerchant(state)
  if (!merchant) return false

  const currentUser = getCurrentUser(state)

  return Boolean(
    currentUser &&
      currentUser.roles &&
      currentUser.roles.find(
        ({role, merchantId}) =>
          role === 'demo-user' && merchantId === merchant.id
      )
  )
}



// WEBPACK FOOTER //
// ./client/redux/modules/currentUser/selectors.js